// Verwaltung (nur GQL): Nutzer einladen, sperren und entsperren.
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import type { DbPortalNutzerEintrag } from '@gql/shared-types';
import { api, ApiFehler } from '../api/client.js';
import { useAuth } from '../auth/AuthKontext.js';
import { VerwaltungReiter } from '../components/VerwaltungReiter.js';
import { zeitpunkt } from '../daten/lieferungen.js';

const ROLLE_TEXT: Record<DbPortalNutzerEintrag['rolle'], string> = { db_user: 'DB-Nutzer', gql_admin: 'GQL' };

interface Einladung {
  nutzerId: string;
  link: string | null;
  mailVersandt: boolean;
}

export function VerwaltungNutzer() {
  const { zustand } = useAuth();
  const ich = zustand.status === 'bereit' ? zustand.ich.nutzer : null;
  const admin = ich?.rolle === 'gql_admin';
  const [liste, setListe] = useState<DbPortalNutzerEintrag[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  const laden = useCallback(() => {
    api<{ nutzer: DbPortalNutzerEintrag[] }>('/admin/nutzer')
      .then((r) => setListe(r.nutzer))
      .catch((f: Error) => setFehler(f.message));
  }, []);
  useEffect(() => {
    if (admin) laden();
  }, [admin, laden]);

  async function sperren(n: DbPortalNutzerEintrag, aktiv: boolean) {
    if (!aktiv && !window.confirm(`${n.email} sperren? Alle Sitzungen enden sofort; Anmeldelinks verfallen.`)) return;
    setLaeuft(n.id);
    setFehler(null);
    try {
      const neu = await api<DbPortalNutzerEintrag>(`/admin/nutzer/${n.id}`, { methode: 'PATCH', json: { aktiv } });
      setListe((l) => l?.map((x) => (x.id === n.id ? neu : x)) ?? null);
    } catch (f) {
      setFehler((f as Error).message);
    } finally {
      setLaeuft(null);
    }
  }

  if (!admin) return <Navigate to="/standorte" replace />;
  return (
    <div className="seite">
      <h1>Nutzer</h1>
      <VerwaltungReiter />
      <NutzerEinladen nachher={laden} />
      {fehler && (
        <p className="fehlertext" role="alert">
          {fehler}
        </p>
      )}
      {!liste && !fehler && (
        <p role="status">
          <span className="spinner" aria-hidden="true" /> Nutzer werden geladen …
        </p>
      )}
      {liste && (
        <div className="tabelle-rahmen">
          <table className="tabelle tabelle-karten">
            <caption className="sr-only">Nutzer</caption>
            <thead>
              <tr>
                <th scope="col">Nutzer</th>
                <th scope="col">Rolle</th>
                <th scope="col">Vertraulichkeit</th>
                <th scope="col">Zuletzt angemeldet</th>
                <th scope="col">Standorte</th>
                <th scope="col">Zugang</th>
              </tr>
            </thead>
            <tbody>
              {liste.map((n) => (
                <tr key={n.id} className={n.aktiv ? '' : 'ist-gesperrt'}>
                  <td data-label="Nutzer">
                    <span>
                      {n.anzeigename ?? n.email}
                      <span className="zelle-hinweis">{[n.anzeigename ? n.email : null, n.organisation].filter(Boolean).join(' · ') || null}</span>
                    </span>
                  </td>
                  <td data-label="Rolle">{ROLLE_TEXT[n.rolle]}</td>
                  <td data-label="Vertraulichkeit">{n.ndaAkzeptiertAm ? `Fassung ${n.ndaVersion ?? '–'}, ${zeitpunkt(n.ndaAkzeptiertAm)}` : 'noch nicht bestätigt'}</td>
                  <td data-label="Angemeldet">{n.letzteAnmeldungAm ? zeitpunkt(n.letzteAnmeldungAm) : 'noch nie'}</td>
                  <td data-label="Standorte">{n.standorte}</td>
                  <td data-label="Zugang">
                    <span className="zugang">
                      <span className={`chip ${n.aktiv ? 'chip-erkannt' : 'chip-fehler'}`}>{n.aktiv ? 'aktiv' : 'gesperrt'}</span>
                      {n.id !== ich?.id && (
                        <button type="button" className="knopf-klein-text" disabled={laeuft === n.id} onClick={() => void sperren(n, !n.aktiv)}>
                          {n.aktiv ? 'Sperren' : 'Entsperren'}
                        </button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NutzerEinladen({ nachher }: { nachher: () => void }) {
  const [offen, setOffen] = useState(false);
  const [email, setEmail] = useState('');
  const [anzeigename, setAnzeigename] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [rolle, setRolle] = useState<DbPortalNutzerEintrag['rolle']>('db_user');
  const [mail, setMail] = useState(true);
  const [rolleAendern, setRolleAendern] = useState(false);
  const [rolleKonflikt, setRolleKonflikt] = useState(false);
  const [ergebnis, setErgebnis] = useState<Einladung | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [kopiert, setKopiert] = useState(false);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setLaeuft(true);
    setFehler(null);
    setErgebnis(null);
    try {
      const r = await api<Einladung>('/admin/nutzer', {
        methode: 'POST',
        json: { email, rolle, anzeigename: anzeigename || null, organisation: organisation || null, mail, rolleAendern },
      });
      setErgebnis(r);
      setRolleKonflikt(false);
      setRolleAendern(false);
      setEmail('');
      setAnzeigename('');
      setOrganisation('');
      nachher();
    } catch (f) {
      if (f instanceof ApiFehler && f.code === 'rolle_abweichend') setRolleKonflikt(true);
      setFehler((f as Error).message);
    } finally {
      setLaeuft(false);
    }
  }

  if (!offen) {
    return (
      <div className="knopfreihe">
        <button type="button" className="knopf" onClick={() => setOffen(true)}>
          Nutzer einladen
        </button>
      </div>
    );
  }
  return (
    <section className="kasten" aria-labelledby="h-einladen">
      <h2 id="h-einladen">Nutzer einladen</h2>
      <form onSubmit={(e) => void absenden(e)}>
        <div className="filterzeile">
          <label className="feld">
            <span className="feld-label">E-Mail-Adresse</span>
            <input type="email" required maxLength={254} autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="feld">
            <span className="feld-label">Name (optional)</span>
            <input type="text" maxLength={120} value={anzeigename} onChange={(e) => setAnzeigename(e.target.value)} />
          </label>
          <label className="feld">
            <span className="feld-label">Organisation (optional)</span>
            <input type="text" maxLength={120} value={organisation} onChange={(e) => setOrganisation(e.target.value)} />
          </label>
          <label className="feld">
            <span className="feld-label">Rolle</span>
            <select value={rolle} onChange={(e) => setRolle(e.target.value as DbPortalNutzerEintrag['rolle'])}>
              <option value="db_user">DB-Nutzer</option>
              <option value="gql_admin">GQL</option>
            </select>
          </label>
        </div>
        <label className="auswahl">
          <input type="checkbox" checked={mail} onChange={(e) => setMail(e.target.checked)} /> Einladung per E-Mail senden (Link 7 Tage gültig)
        </label>
        {rolleKonflikt && (
          <label className="auswahl">
            <input type="checkbox" checked={rolleAendern} onChange={(e) => setRolleAendern(e.target.checked)} /> Rolle des bestehenden Nutzers ausdrücklich ändern
          </label>
        )}
        <p className="hinweis-klein">
          DB-Nutzer sehen nur die ihnen zugeordneten Standorte; GQL sieht alles und verwaltet. Ein gesperrter Nutzer wird durch eine neue Einladung
          wieder freigeschaltet.
        </p>
        <div className="knopfreihe">
          <button type="submit" className="knopf" disabled={laeuft}>
            {laeuft ? 'Wird eingeladen …' : 'Einladen'}
          </button>
          <button type="button" className="knopf-sekundaer knopf-auto" onClick={() => setOffen(false)}>
            Schließen
          </button>
        </div>
      </form>
      {fehler && (
        <p className="fehlertext" role="alert">
          {fehler}
        </p>
      )}
      {ergebnis && (
        <div className="hinweis-kasten" role="status">
          {ergebnis.mailVersandt || !ergebnis.link ? (
            <p>Die Einladung wurde per E-Mail versandt.</p>
          ) : (
            <>
              <p>
                Es wurde <strong>keine E-Mail</strong> versandt. Bitte den Einladungslink auf sicherem Weg weitergeben (7 Tage gültig, nur einmal
                verwendbar):
              </p>
              <div className="link-zeile">
                <input type="text" readOnly value={ergebnis.link} aria-label="Einladungslink" onFocus={(e) => e.target.select()} />
                <button
                  type="button"
                  className="knopf-sekundaer knopf-auto"
                  onClick={() =>
                    void navigator.clipboard?.writeText(ergebnis.link ?? '').then(
                      () => setKopiert(true),
                      () => setKopiert(false),
                    )
                  }
                >
                  {kopiert ? 'Kopiert' : 'Kopieren'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
